import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import get from "lodash/get";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { formatError } from "helpers/formatError";
import { useDebounce } from "hooks/useDebounce";

import { REQUIRED_MARK } from "./fieldClasses";

const SEARCH_DEBOUNCE_MS = 300;

const formatRecordLabel = (id, name) =>
  name ? `[ID: ${id}] ${name}` : `[ID: ${id}]`;

const toItem = (id, name) => ({
  value: id,
  label: formatRecordLabel(id, name),
});

/**
 * Async multi-record picker for relationship fields. The field value is an
 * array of record ids. `display` seeds the labels of the ids already selected
 * in edit mode, because the search results only cover the current query.
 */
const FormRecordMultiPicker = ({
  name,
  label,
  required,
  fetchOptions,
  optionsKey,
  display,
  placeholder = "Search...",
  disabled,
  readOnly,
  id,
  onValueChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [open, setOpen] = useState(false);
  const [pickedById, setPickedById] = useState(() => new Map());

  const {
    data,
    isFetching,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["record-picker", optionsKey ?? name, debouncedSearch],
    queryFn: () => fetchOptions(debouncedSearch || null),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const fetched = useMemo(
    () => (Array.isArray(data) ? data : []).map(r => toItem(r.id, r.name)),
    [data]
  );

  const seedById = useMemo(
    () => new Map((display ?? []).map(d => [d.id, toItem(d.id, d.label)])),
    [display]
  );

  const resolveItem = value =>
    pickedById.get(value) ??
    seedById.get(value) ??
    fetched.find(it => it.value === value) ??
    toItem(value);

  const errorMessage = isError
    ? formatError(queryError, "Could not load options")
    : null;
  const isBusy = isFetching || isError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const current = Array.isArray(field.value) ? field.value : [];
        const selectedItems = current.map(resolveItem);
        const selectedValues = new Set(current);
        const availableItems = fetched.filter(
          it => !selectedValues.has(it.value)
        );

        const handleValueChange = next => {
          const nextItems = Array.isArray(next) ? next : [];
          setPickedById(prev => {
            const merged = new Map(prev);
            nextItems.forEach(it => merged.set(it.value, it));
            return merged;
          });
          const nextIds = nextItems.map(it => it.value);
          field.onChange(nextIds);
          onValueChange?.(nextIds);
        };

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <Combobox
              items={availableItems}
              value={selectedItems}
              onValueChange={handleValueChange}
              onInputValueChange={setSearch}
              onOpenChange={setOpen}
              isItemEqualToValue={(a, b) => a?.value === b?.value}
              filter={null}
              multiple
              disabled={disabled}
              readOnly={readOnly}
            >
              <ComboboxChips>
                <ComboboxValue>
                  {selected =>
                    selected.map(it => (
                      <ComboboxChip key={it.value} showRemove={!readOnly}>
                        {it.label}
                      </ComboboxChip>
                    ))
                  }
                </ComboboxValue>
                <ComboboxChipsInput
                  id={inputId}
                  placeholder={current.length === 0 ? placeholder : ""}
                  aria-required={required || undefined}
                  aria-invalid={error ? true : undefined}
                  onBlur={field.onBlur}
                />
              </ComboboxChips>
              <ComboboxContent>
                <ComboboxPrimitive.Status className="tw:sr-only">
                  {isFetching ? "Searching…" : null}
                </ComboboxPrimitive.Status>
                {errorMessage && (
                  <div className="tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground">
                    {errorMessage}
                  </div>
                )}
                <ComboboxEmpty>{isBusy ? null : "No matches"}</ComboboxEmpty>
                <ComboboxList className={isBusy ? "tw:hidden" : undefined}>
                  {item => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormRecordMultiPicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  fetchOptions: PropTypes.func.isRequired,
  optionsKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  display: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string,
    })
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  onValueChange: PropTypes.func,
};

export default FormRecordMultiPicker;
